import { TextField as MuiTextField } from '@material-ui/core';
import { IconButton } from '@monorepo/shared/componentsV2/Button/IconButton';
import {
  ReorderableElement,
  ReorderableList,
} from '@monorepo/shared/componentsV2/dragAndDrop/ReorderableList';
import {
  DraggableElement,
  DragHandle,
  ListElement,
} from '@monorepo/shared/componentsV2/dragAndDrop/styled';
import { List } from '@monorepo/shared/componentsV2/List';
import { VisuallyHidden } from '@monorepo/shared/componentsV2/VisuallyHidden';
import { lookFor } from '@monorepo/shared/utils/lookFor';
import EyeClosedIcon from '@svg/eye-closed.svg';
import EyeOpenIcon from '@svg/eye-open.svg';
import truncate from 'lodash.truncate';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import { DisplayQueryColumn } from '../../ViewResultsTable/resultsMapper';

// ========================
// Styles.
// ========================
const StyledListContainer = styled.div`
  width: 100%;
  max-height: 42vh;
  padding: 0.5rem 0;
  overflow-y: scroll;

  & ${DraggableElement} {
    width: calc(100% - 1rem);
    margin: 0.75rem 0.5rem;

    & ${DragHandle} {
      left: 0;
      opacity: 0.5;
      transform: scale(0.8);
    }

    &:hover ${DragHandle} {
      opacity: 1;
    }
  }
`;

const StyledRenameListContainer = styled(StyledListContainer)`
  padding: 0.25rem 0;
`;

const StyledIconButton = styled(IconButton)`
  position: absolute;
  top: -0.4rem;
  right: 0;

  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

const StyledUnorderableListElement = styled(ListElement)`
  position: relative;
  width: calc(100% - 0.5rem);

  & .MuiButtonBase-root {
    opacity: 0;
    transition: opacity 0.1s ease-in-out;
  }

  &:hover .MuiButtonBase-root {
    opacity: 1;
  }
`;

const StyledReorderableColumnListItem = styled.div`
  display: flex;
  padding: 0.125rem 0;
  margin-left: 2rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray666};
  white-space: pre-wrap;
  -webkit-text-stroke: 0.5px transparent;

  /* stylelint-disable no-descending-specificity */
  & .MuiButtonBase-root {
    opacity: 0;
    transition: opacity 0.1s ease-in-out;
  }

  &:hover .MuiButtonBase-root {
    opacity: 1;
  }
  /* stylelint-enable no-descending-specificity */
`;

const StyledListItemLabel = styled.div`
  padding: 0.125rem 0;
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  margin-left: 2.5rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.gray666};
  white-space: pre-wrap;
  -webkit-text-stroke: 0.5px transparent;
`;

const StyledUnrenameableListItemLabel = styled(StyledListItemLabel)`
  margin-left: 2rem;
`;

const StyledNoResults = styled(StyledListItemLabel)`
  font-style: italic;
  font-weight: 400;
`;

const StyledMuiTextField = styled(MuiTextField)`
  width: calc(100% - 1.5rem);
  margin-bottom: 0.5rem;
  margin-left: 1.25rem;

  & .MuiInputBase-root {
    font-size: 0.875rem;

    & input {
      padding: 0.4rem 0.75rem;
      -webkit-text-stroke: transparent;
    }
  }
`;

// ========================
// Rendered components.
// ========================
function getDisplayColumnLabel(column: DisplayQueryColumn) {
  return truncate(column.columnLabel.trim() || column.originalColumnLabel, {
    length: 20,
  });
}

export function isColumnReorderable(column: DisplayQueryColumn) {
  return !column.isTimeSeriesDate && !column.isHidden;
}

function ViewColumnShowHideButton({
  column,
  disabled,
  onClick,
}: {
  column: DisplayQueryColumn;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <StyledIconButton disabled={disabled} onClick={onClick}>
      {column.isHidden ? (
        <>
          <VisuallyHidden>Show Column</VisuallyHidden>
          <EyeOpenIcon />
        </>
      ) : (
        <>
          <VisuallyHidden>Hide Column</VisuallyHidden>
          <EyeClosedIcon />
        </>
      )}
    </StyledIconButton>
  );
}

function UnorderableColumnListItem({
  column,
  disableShowHideButton,
  toggleColumnVisibility,
  className,
}: {
  column: DisplayQueryColumn;
  disableShowHideButton: boolean;
  toggleColumnVisibility: (column: DisplayQueryColumn) => void;
  className?: string;
}) {
  return (
    <StyledUnorderableListElement key={column.columnName} className={className}>
      <StyledListItemLabel>{getDisplayColumnLabel(column)}</StyledListItemLabel>
      <ViewColumnShowHideButton
        column={column}
        disabled={disableShowHideButton}
        onClick={() => {
          toggleColumnVisibility(column);
        }}
      />
    </StyledUnorderableListElement>
  );
}

const FilteredColumnListItem = styled(UnorderableColumnListItem)`
  & ${StyledListItemLabel} {
    margin-left: 1.5rem;
  }
`;

export function ReorderableColumnListItem({
  column,
  disableShowHideButton,
  toggleColumnVisibility,
}: {
  column: DisplayQueryColumn;
  disableShowHideButton: boolean;
  toggleColumnVisibility: (column: DisplayQueryColumn) => void;
}) {
  return (
    <StyledReorderableColumnListItem>
      <div>{getDisplayColumnLabel(column)}</div>
      <ViewColumnShowHideButton
        column={column}
        disabled={disableShowHideButton}
        onClick={() => {
          toggleColumnVisibility(column);
        }}
      />
    </StyledReorderableColumnListItem>
  );
}

export function VisibleColumnList({
  visibleUnorderableColumns,
  visibleOrderableColumns,
  isSaving,
  updateColumnOrder,
  toggleColumnVisibility,
  filter,
}: {
  visibleUnorderableColumns: DisplayQueryColumn[];
  visibleOrderableColumns: (ReorderableElement & DisplayQueryColumn)[];
  isSaving: boolean;
  updateColumnOrder: (orderedElements: string[]) => void;
  toggleColumnVisibility: (column: DisplayQueryColumn) => void;
  filter: string;
}) {
  const filteredVisibleColumns = useMemo(
    () =>
      [...visibleUnorderableColumns, ...visibleOrderableColumns].filter(
        (column) => lookFor(filter, column, ['columnLabel']),
      ),
    [filter, visibleUnorderableColumns, visibleOrderableColumns],
  );
  return (
    <StyledListContainer>
      {filter.trim() === '' ? (
        <>
          <List>
            {visibleUnorderableColumns.map((column) => (
              <UnorderableColumnListItem
                key={column.columnName}
                column={column}
                disableShowHideButton
                toggleColumnVisibility={toggleColumnVisibility}
              />
            ))}
          </List>
          <ReorderableList
            orderedElements={visibleOrderableColumns}
            onOrderChange={updateColumnOrder}
            uniqueDragListId="reorderableColumnNames"
            listAriaLabel="Reorderable Column Names"
            usePortal
            disableDragging={isSaving}
          />
        </>
      ) : (
        <List>
          {filteredVisibleColumns.length > 0 ? (
            filteredVisibleColumns.map((column) => (
              <FilteredColumnListItem
                column={column}
                disableShowHideButton={isSaving || !isColumnReorderable(column)}
                toggleColumnVisibility={toggleColumnVisibility}
              />
            ))
          ) : (
            <StyledUnorderableListElement>
              <StyledNoResults>No results.</StyledNoResults>
            </StyledUnorderableListElement>
          )}
        </List>
      )}
    </StyledListContainer>
  );
}

export function HiddenColumnList({
  hiddenColumns,
  isSaving,
  toggleColumnVisibility,
  filter,
}: {
  hiddenColumns: DisplayQueryColumn[];
  isSaving: boolean;
  toggleColumnVisibility: (column: DisplayQueryColumn) => void;
  filter: string;
}) {
  const filteredHiddenColumns = useMemo(
    () =>
      hiddenColumns.filter((column) =>
        lookFor(filter, column, ['columnLabel']),
      ),
    [filter, hiddenColumns],
  );
  return (
    <StyledListContainer>
      <List>
        {filteredHiddenColumns.length > 0 ? (
          filteredHiddenColumns.map((column) => (
            <FilteredColumnListItem
              column={column}
              disableShowHideButton={isSaving}
              toggleColumnVisibility={toggleColumnVisibility}
            />
          ))
        ) : (
          <StyledUnorderableListElement>
            <StyledNoResults>No results.</StyledNoResults>
          </StyledUnorderableListElement>
        )}
      </List>
    </StyledListContainer>
  );
}

export function RenameColumnList({
  visibleUnorderableColumns,
  visibleOrderableColumns,
  renameColumn,
  isSaving,
}: {
  visibleUnorderableColumns: DisplayQueryColumn[];
  visibleOrderableColumns: DisplayQueryColumn[];
  renameColumn: (column: DisplayQueryColumn, alias: string) => void;
  isSaving: boolean;
}) {
  return (
    <StyledRenameListContainer>
      <List>
        {visibleUnorderableColumns.map((column) => (
          <StyledUnorderableListElement key={column.columnName}>
            <StyledUnrenameableListItemLabel>
              {getDisplayColumnLabel(column)}
            </StyledUnrenameableListItemLabel>
          </StyledUnorderableListElement>
        ))}
        {visibleOrderableColumns.map((column) => (
          <StyledUnorderableListElement key={column.columnName}>
            <StyledMuiTextField
              value={column.columnLabel}
              placeholder={column.originalColumnLabel}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                renameColumn(column, event.target.value);
              }}
              onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                renameColumn(column, event.target.value.trim());
              }}
              disabled={isSaving}
              variant="outlined"
              size="small"
            />
          </StyledUnorderableListElement>
        ))}
      </List>
    </StyledRenameListContainer>
  );
}
